import { useAccidentsServiceAccidentsContractTypesList } from '@insurance/api/queries'
import { RadioCardGroup, RadioCardGroupOption } from '@insurance/components'
import { useValidationsField } from '@insurance/hooks'
import { Form } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccidentInsuranceValues } from './AccidentInsuranceContext'
import { ContractTypeCode } from './type'
import {
  formProps,
  labelPersonsContractType,
  personsRestrictions
} from './utils'

export const ContractType = () => {
  const { isEditable, order } = useAccidentInsuranceValues()
  const { t } = useTranslation()
  const { required } = useValidationsField()
  const {
    contractTypeProps,
    personsCountProps,
    halfDayCoverageProps,
    prevPersonsCountKey
  } = formProps(t)
  const contractType = Form.useWatch(contractTypeProps.name)
  const formInstance = Form.useFormInstance()
  const [auxOptions, setAuxOptions] = useState<RadioCardGroupOption[]>([])

  const { data } = useAccidentsServiceAccidentsContractTypesList({
    ordering: 'persons'
  })

  const options = data
    ?.filter(({ code }) =>
      Object.values(ContractTypeCode).includes(code as ContractTypeCode)
    )
    ?.reduce((previous, current) => {
      previous = previous.some(({ value }) => current.code === value)
        ? previous
        : [
            ...previous,
            {
              label: t(current.code?.toLowerCase() || ''),
              value: current.code!,
              colProps: { xs: 24, lg: 8 }
            }
          ]
      return previous
    }, [] as RadioCardGroupOption[])

  useEffect(() => {
    if (!data || !contractType) return

    const foundData = data.filter(({ code }) => code === contractType)
    const personCount = formInstance.getFieldValue(personsCountProps.name)
    const orderPersonsCount = order?.accidents?.persons?.length || 0

    const { personRange } = personsRestrictions(
      contractType,
      orderPersonsCount,
      orderPersonsCount
    )

    if (order) {
      formInstance.setFieldValue(prevPersonsCountKey.name, personRange)
    }

    if (!foundData.some(({ persons }) => personCount === persons)) {
      formInstance.setFieldValue(
        personsCountProps.name,
        order ? personRange : foundData?.[0]?.persons
      )
    }

    if (foundData.length <= 1) return setAuxOptions([])

    const auxOptions = foundData.map(({ code, persons }) => ({
      label: labelPersonsContractType(t, persons)[code as ContractTypeCode],
      value: persons,
      colProps: { xs: 24, lg: 8 }
    }))

    setAuxOptions(auxOptions)
  }, [
    contractType,
    data,
    formInstance,
    personsCountProps.name,
    t,
    order,
    prevPersonsCountKey.name
  ])

  const halfDayCoverageOptions = [
    { value: false, label: '24/24' },
    { value: true, label: '12/24' }
  ]

  const showHalfDayCoverage = contractType === ContractTypeCode.COLLECTIVE

  return (
    <>
      <Form.Item {...contractTypeProps} rules={[required]}>
        <RadioCardGroup disabled={isEditable} options={options} />
      </Form.Item>

      {!!auxOptions?.length && (
        <Form.Item {...personsCountProps} rules={[required]}>
          <RadioCardGroup disabled={isEditable} options={auxOptions} />
        </Form.Item>
      )}

      {showHalfDayCoverage && (
        <Form.Item {...halfDayCoverageProps}>
          <RadioCardGroup
            disabled={isEditable}
            options={halfDayCoverageOptions}
          />
        </Form.Item>
      )}
    </>
  )
}
